import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ASSIGNABLE_ROLES,
  type LabelAccessRule,
  type MemberRole,
  type Principal,
  type PrincipalType,
  PrincipalSelect,
  useBoardLabelCatalog,
  useCreateLabelAccessRule,
  useCustomRoles,
  useDeleteLabelAccessRule,
  useLabelAccessRules,
  useSetLabelAccessRuleActive,
  useTeams,
} from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, Combobox } from '@/shared/ui';

import styles from './Admin.module.css';

type Kind = 'database' | 'document';

/**
 * Access by label for DATABASES and NOTES. A rule has three separate parts, in this order:
 * WHICH resources (a kind and the labels that select it), WHO receives access (a team, a
 * workspace role or a custom role) and WHAT LEVEL they get (chosen per kind: a database role and a
 * note role are different things even when they share a name). A label by itself grants nothing -
 * only a rule listed here does. The rule feeds the same list as access given to one database or
 * note, so a confidential database, an explicit role and a denial keep their authority; a rule
 * that is switched off stays listed and grants nothing. The preview only counts what the rule
 * would reach; it never lists names of resources the administrator may not be allowed to see.
 */
export function LabelRulesPanel({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const rules = useLabelAccessRules(workspace.id);
  const teams = useTeams(workspace.id);
  const labels = useBoardLabelCatalog(workspace.id);
  const customRoles = useCustomRoles(workspace.id);
  const create = useCreateLabelAccessRule(workspace.id);
  const remove = useDeleteLabelAccessRule(workspace.id);
  const toggle = useSetLabelAccessRuleActive(workspace.id);
  const [kind, setKind] = useState<Kind>('database');
  const [principalType, setPrincipalType] = useState<PrincipalType>('team');
  const [principal, setPrincipal] = useState<Principal | null>(null);
  // one granted role PER resource kind: databases and notes never share a role selection
  const [grantedByKind, setGrantedByKind] = useState<Record<Kind, MemberRole>>({
    database: 'editor',
    document: 'editor',
  });
  const granted = grantedByKind[kind];
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [match, setMatch] = useState<'any' | 'all'>('any');
  const [preview, setPreview] = useState<number | null>(null);

  const labelName = (id: string) => (labels.data ?? []).find((l) => l.id === id)?.name ?? '…';
  const subjectName = (rule: LabelAccessRule): string =>
    rule.subject_kind === 'team'
      ? ((teams.data ?? []).find((x) => x.id === rule.team_id)?.name ?? '…')
      : rule.subject_kind === 'role'
        ? t(`roles.${rule.subject_role ?? 'member'}`)
        : ((customRoles.data ?? []).find((x) => x.id === rule.custom_role_id)?.name ?? '…');
  const principalName = (value: Principal): string =>
    value.kind === 'team'
      ? ((teams.data ?? []).find((x) => x.id === value.id)?.name ?? '…')
      : value.kind === 'role'
        ? t(`roles.${value.role}`)
        : ((customRoles.data ?? []).find((x) => x.id === value.id)?.name ?? '…');
  const payload = (dryRun: boolean) => ({
    resource_kind: kind,
    subject_kind: principal?.kind ?? 'team',
    ...(principal?.kind === 'team' && { team_id: principal.id }),
    ...(principal?.kind === 'role' && { subject_role: principal.role }),
    ...(principal?.kind === 'custom_role' && { custom_role_id: principal.id }),
    granted_role: granted,
    label_ids: labelIds,
    label_match: match,
    dry_run: dryRun,
  });
  const ready = principal !== null && labelIds.length > 0;
  const roleOptions = ASSIGNABLE_ROLES.map((r) => ({
    value: r,
    label: t(`roles.${r}`),
    description: (t as unknown as (key: string) => string)(`labelRules.roleHelp.${kind}.${r}`),
  }));

  return (
    <section className={styles.card} aria-labelledby="label-rules-title" data-testid="label-rules">
      <h3 id="label-rules-title">{t('labelRules.title')}</h3>
      <p className={styles.note}>{t('labelRules.hint')}</p>
      <details className={styles.precedence}>
        <summary>{t('labelRules.precedence.title')}</summary>
        <ul>
          {(['confidential', 'explicit', 'denial', 'groups', 'off'] as const).map((key) => (
            <li key={key}>{t(`labelRules.precedence.${key}`)}</li>
          ))}
        </ul>
      </details>
      <ul className={styles.list}>
        {(rules.data ?? []).map((rule) => (
          <li
            key={rule.id}
            className={styles.row}
            data-testid="label-rule"
            data-active={rule.is_active}
          >
            <span className={styles.grow}>
              {t('labelRules.ruleText', {
                subject: subjectName(rule),
                role: t(`roles.${rule.granted_role}`),
                kind: t(`labelRules.kinds.${rule.resource_kind}`),
              })}
              <span className={styles.meta}>
                <span className={styles.ruleBadge}>
                  {t(`labelRules.subjectKinds.${rule.subject_kind}`)}
                </span>
                {rule.label_ids.map((id, index) => {
                  const label = (labels.data ?? []).find((l) => l.id === id);
                  return (
                    <span key={id} className={styles.ruleLabel}>
                      {index > 0 && (
                        <span aria-hidden>{rule.label_match === 'all' ? ' + ' : ' / '}</span>
                      )}
                      {label && (
                        <span
                          aria-hidden
                          className={styles.swatch}
                          style={{ background: label.color }}
                        />
                      )}
                      {labelName(id)}
                    </span>
                  );
                })}
                {' · '}
                {t('labelRules.matching', { count: rule.matching_count })}
              </span>
            </span>
            <label className={styles.ruleSwitch}>
              <input
                type="checkbox"
                role="switch"
                checked={rule.is_active}
                disabled={toggle.isPending}
                aria-label={t('labelRules.toggleName', {
                  rule: t('labelRules.ruleText', {
                    subject: subjectName(rule),
                    role: t(`roles.${rule.granted_role}`),
                    kind: t(`labelRules.kinds.${rule.resource_kind}`),
                  }),
                })}
                onChange={(event) => {
                  toggle.mutate({ ruleId: rule.id, active: event.target.checked });
                }}
              />
              <span>{t(rule.is_active ? 'labelRules.state.active' : 'labelRules.state.off')}</span>
            </label>
            <Button
              size="sm"
              variant="ghost"
              disabled={remove.isPending}
              onClick={() => {
                remove.mutate(rule.id);
              }}
            >
              {t('workspaceAdmin.deleteRule')}
            </Button>
          </li>
        ))}
        {(rules.data ?? []).length === 0 && <li className={styles.note}>{t('labelRules.none')}</li>}
      </ul>

      <div className={styles.ruleSteps}>
        <fieldset className={styles.step}>
          <legend>
            <span className={styles.stepNo}>1</span> {t('labelRules.steps.resources')}
          </legend>
          <p className={styles.note}>{t('labelRules.stepHint.resources')}</p>
          <div className={styles.field2}>
            <span className={styles.fieldLabel}>{t('labelRules.on')}</span>
            <Combobox
              label={t('labelRules.on')}
              value={kind}
              size="sm"
              options={[
                { value: 'database', label: t('labelRules.kinds.database') },
                { value: 'document', label: t('labelRules.kinds.document') },
              ]}
              onChange={(next) => {
                setKind(next === 'document' ? 'document' : 'database');
                setPreview(null);
              }}
            />
          </div>
          <div className={styles.field2}>
            <span className={styles.fieldLabel}>{t('workspaceAdmin.ruleMatch')}</span>
            <Combobox
              label={t('workspaceAdmin.ruleMatch')}
              value={match}
              size="sm"
              options={[
                { value: 'any', label: t('filter.matchAny') },
                { value: 'all', label: t('filter.matchAll') },
              ]}
              onChange={(next) => {
                setMatch(next === 'all' ? 'all' : 'any');
                setPreview(null);
              }}
            />
          </div>
          <div className={styles.labelPick} role="group" aria-label={t('labelRules.labelsLegend')}>
            <span className={styles.fieldLabel}>{t('labelRules.labelsLegend')}</span>
            <div className={styles.labelChips}>
              {(labels.data ?? []).map((l) => {
                const on = labelIds.includes(l.id);
                return (
                  <button
                    key={l.id}
                    type="button"
                    aria-pressed={on}
                    className={`${styles.labelChip} ${on ? styles.labelChipOn : ''}`}
                    onClick={() => {
                      setLabelIds(on ? labelIds.filter((x) => x !== l.id) : [...labelIds, l.id]);
                      setPreview(null);
                    }}
                  >
                    <span className={styles.swatch} style={{ background: l.color }} />
                    {l.name}
                  </button>
                );
              })}
              {(labels.data ?? []).length === 0 && (
                <span className={styles.note}>{t('workspaceAdmin.noLabelsYet')}</span>
              )}
            </div>
          </div>
        </fieldset>

        <fieldset className={styles.step}>
          <legend>
            <span className={styles.stepNo}>2</span> {t('labelRules.steps.recipient')}
          </legend>
          <p className={styles.note}>{t('labelRules.stepHint.recipient')}</p>
          <div className={styles.field2}>
            <span className={styles.fieldLabel}>{t('access.principal.who')}</span>
            <PrincipalSelect
              type={principalType}
              onTypeChange={setPrincipalType}
              value={principal}
              onChange={setPrincipal}
              teams={teams.data ?? []}
              customRoles={customRoles.data ?? []}
            />
          </div>
        </fieldset>

        <fieldset className={styles.step}>
          <legend>
            <span className={styles.stepNo}>3</span> {t('labelRules.steps.level')}
          </legend>
          <p className={styles.note}>{t('labelRules.stepHint.level')}</p>
          <div className={styles.field2}>
            <span className={styles.fieldLabel}>{t(`labelRules.grantsOn.${kind}`)}</span>
            <Combobox
              label={t(`labelRules.grantsOn.${kind}`)}
              value={granted}
              size="sm"
              searchable={false}
              options={roleOptions}
              onChange={(next) => {
                setGrantedByKind((prev) => ({ ...prev, [kind]: next as MemberRole }));
              }}
            />
          </div>
        </fieldset>
      </div>

      <p className={styles.ruleSummary} data-testid="label-rule-summary">
        {ready
          ? t('labelRules.summary', {
              subject: principalName(principal),
              subjectKind: t(`labelRules.subjectKinds.${principal.kind}`),
              role: t(`roles.${granted}`),
              kind: t(`labelRules.kinds.${kind}`),
              match: t(`labelRules.matchPhrase.${match}`),
              labels: labelIds.map(labelName).join(', '),
            })
          : t('labelRules.summaryIncomplete')}
      </p>
      {preview !== null && (
        <p className={styles.note} role="status">
          {t('labelRules.preview', { count: preview })}
        </p>
      )}
      <FormError error={create.error ?? remove.error ?? toggle.error} />
      <div className={styles.actions}>
        <Button
          disabled={!ready || create.isPending}
          onClick={() => {
            create.mutate(payload(true), {
              onSuccess: (result) => {
                setPreview(result.matching_count);
              },
            });
          }}
        >
          {t('labelRules.previewAction')}
        </Button>
        <Button
          variant="primary"
          disabled={!ready || create.isPending}
          onClick={() => {
            create.mutate(payload(false), {
              onSuccess: () => {
                setPreview(null);
                setLabelIds([]);
              },
            });
          }}
        >
          {t('workspaceAdmin.addRule')}
        </Button>
      </div>
    </section>
  );
}
